import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type RequestsCreateTryoutCategory from "@/types/serializers/RequestsCreateTryoutCategory"

interface CategoryFormProps {
  tryoutSlug: string
  onCancel?: () => void
  onSuccess?: () => void
}

const MIN_DATE = new Date().toISOString().split("T")[0]

export default function CategoryForm({
  tryoutSlug,
  onCancel,
  onSuccess,
}: CategoryFormProps) {
  const { data, setData, post, processing, errors, reset } =
    useForm<RequestsCreateTryoutCategory>({
      tryout_category: {
        birth_year_from: 2010,
        birth_year_to: 2012,
        date: "",
        start_time: "09:00",
        end_time: "12:00",
        max_spots: null,
      },
    })

  const setField = (
    field: keyof typeof data.tryout_category,
    value: string | number | null,
  ) =>
    setData("tryout_category", {
      ...data.tryout_category,
      [field]: value,
    } as typeof data.tryout_category)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(`/pruebas/${tryoutSlug}/categorias`, {
      onSuccess: () => {
        reset()
        onSuccess?.()
      },
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="birth_year_from">Año nacimiento desde</Label>
          <Input
            id="birth_year_from"
            type="number"
            value={data.tryout_category.birth_year_from}
            onChange={(e) =>
              setField("birth_year_from", parseInt(e.target.value))
            }
            min={2000}
            max={2020}
          />
          {errors["tryout_category.birth_year_from"] && (
            <p className="mt-1 text-sm text-red-500">
              {errors["tryout_category.birth_year_from"]}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="birth_year_to">Año nacimiento hasta</Label>
          <Input
            id="birth_year_to"
            type="number"
            value={data.tryout_category.birth_year_to}
            onChange={(e) =>
              setField("birth_year_to", parseInt(e.target.value))
            }
            min={2000}
            max={2020}
          />
          {errors["tryout_category.birth_year_to"] && (
            <p className="mt-1 text-sm text-red-500">
              {errors["tryout_category.birth_year_to"]}
            </p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="date">Fecha</Label>
        <Input
          id="date"
          type="date"
          min={MIN_DATE}
          value={data.tryout_category.date}
          onChange={(e) => setField("date", e.target.value)}
        />
        {errors["tryout_category.date"] && (
          <p className="mt-1 text-sm text-red-500">
            {errors["tryout_category.date"]}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="start_time">Hora inicio</Label>
          <Input
            id="start_time"
            type="time"
            value={data.tryout_category.start_time}
            onChange={(e) => setField("start_time", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="end_time">Hora fin</Label>
          <Input
            id="end_time"
            type="time"
            value={data.tryout_category.end_time}
            onChange={(e) => setField("end_time", e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="max_spots">
          Cupos máximos (dejar vacío = ilimitado)
        </Label>
        <Input
          id="max_spots"
          type="number"
          value={data.tryout_category.max_spots ?? ""}
          onChange={(e) =>
            setField(
              "max_spots",
              e.target.value ? parseInt(e.target.value) : null,
            )
          }
          min={1}
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={processing}>
          Agregar categoría
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        )}
      </div>
    </form>
  )
}
